import React, { cloneElement, FC } from 'react';
import {
  List,
  TextField,
  DateField,
  useListContext,
  TopToolbar,
  sanitizeListRestProps,
  Pagination,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import { ArchivedJobsFilters } from '../filters/archived-jobs-filters';
import { PurchaseOrderField } from '../components/PurchaseOrderField';
import { makeStyles } from '@material-ui/core';
import { exporter } from 'providers/export';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { AppRole } from 'providers/roles';
import CustomExportButton from 'components/CustomExportButton';
import { SupervisorField } from 'components/SupervisorListItems';

const useStyles = makeStyles({
  content: {
    '@media(max-width: 1550px)': {
      overflow: 'auto',
    },
  },
  contentClosed: {
    '@media(max-width: 1550px)': {
      overflow: 'auto',
    },
  },
  root: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 260px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  rootClosed: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 125px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
});

const ListActions: FC<any> = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    total,
  } = useListContext();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

export const ArchivedJobsList: FC<any> = props => {
  const { hasRole } = PermissionsProvider.useContainer();
  const classes = useStyles(props);
  const { opened } = UIProvider.useContainer();

  const isSupervisor = hasRole(AppRole.Supervisor);

  return (
    <List
      className="list"
      {...props}
      exporter={exporter('job-archived-sheet')}
      filters={<ArchivedJobsFilters />}
      sort={{
        field: 'lastEventAt',
        order: 'DESC',
      }}
      actions={<ListActions maxResults={20000} />}
      classes={{
        content: opened ? classes.content : classes.contentClosed,
        root: opened ? classes.root : classes.rootClosed,
      }}
      pagination={<Pagination rowsPerPageOptions={[10, 25]} />}
      perPage={10}
    >
      <Datagrid
        rowClick={(id, _basePath, record) => {
          const route =
            record.phaseStructureType === 'DrywallAndPaint'
              ? 'job-master-sheet-v2'
              : 'job-master-sheet';
          return `/${route}/${record.jobId || id}`;
        }}
        isHeaderSelectable={false}
        isRowSelectable={() => false}
      >
        {!isSupervisor && <SupervisorField label="Supervisors" />}
        <TextField source="jobLot" label="Lot" />
        <TextField source="jobBlock" label="Block" />
        <PurchaseOrderField
          source="purchaseOrderNumber"
          label="Purchase Order #"
        />
        <TextField source="builderName" label="Builder" />
        <TextField source="subdivisionName" label="Subdivision" />
        <TextField source="address1" label="Address" />
        <DateField source="poSignedAt" label="Project Start Date" />
        <DateField source="lastEventAt" label="Last Update" />
      </Datagrid>
    </List>
  );
};
